import React from "react";

/**
 * Standardized Status Badge Component
 * Provides consistent styling for status indicators across all tables
 */
export default function StatusBadge({ 
  status, 
  variant = "default", 
  showIcon = false, 
  icon: Icon = null,
  className = "" 
}) {
  const getStatusConfig = (status, variant) => {
    const baseConfig = {
      // Default status colors
      active: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
      confirmed: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      delivering: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
      picking: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
      qc: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
      packing: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
      suspended: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
      returning: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
      checking: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
      refunded: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
      enabled: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      disabled: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
      // Role-based colors
      'super admin': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
      admin: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
      seller: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      service: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
      picker: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
      packer: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200' },
      'delivery admin': { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-200' },
      delivery: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200' },
    };

    return baseConfig[status?.toLowerCase()] || { 
      bg: 'bg-gray-100', 
      text: 'text-gray-800', 
      border: 'border-gray-200' 
    };
  };

  const config = getStatusConfig(status, variant);
  const hasBorder = variant === 'bordered';
  
  const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
  const variantClasses = hasBorder ? `border ${config.border}` : '';
  const colorClasses = `${config.bg} ${config.text}`;
  
  return (
    <span className={`${baseClasses} ${colorClasses} ${variantClasses} ${className}`}>
      {showIcon && Icon && <Icon className="w-3 h-3 mr-1" />}
      {status || 'Unknown'}
    </span>
  );
}

/**
 * Status Badge with Icon Component
 * Pre-configured status badge with icons
 */
export function StatusBadgeWithIcon({ status, className = "" }) {
  const getStatusIcon = (status) => {
    const iconMap = {
      active: '✓',
      completed: '✓',
      confirmed: '✓',
      delivered: '✓',
      pending: '⏳',
      picking: '📦',
      qc: '🔍',
      packing: '📋',
      delivering: '🚚',
      cancelled: '❌',
      failed: '❌',
      suspended: '⛔',
      returning: '↩️',
      checking: '🔍',
      refunded: '💰',
    };
    return iconMap[status?.toLowerCase()] || '•';
  };

  return (
    <StatusBadge 
      status={status} 
      className={className}
    >
      <span className="mr-1">{getStatusIcon(status)}</span>
    </StatusBadge>
  );
}
